import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/layout/PageHeader';
import { PricingTiers } from '../components/pricing/PricingTiers';
import { CtaBand } from '../components/ui/CtaBand';
import { FaqList } from '../components/ui/FaqList';
import { SectionHeading } from '../components/ui/SectionHeading';
import { faqs } from '../data/site';
import { categories } from '../data/categories';
import { products } from '../data/products';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function Pricing() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Pricing — ${brand.name}`,
    description: 'Membership from $39/mo, medication billed separately and priced in the open. Your rate is locked for a year.'
  });
  const rows = [...products].sort((a, b) => a.name.localeCompare(b.name));
  return <main data-id="element-461" data-mp="1ajmjxlq34fc9">
      <PageHeader eyebrow="Pricing" title="One price, locked for a year." intro="Your doctor and your medication are billed separately, so you can see exactly what you are paying for and why. No dispensing fee, no charge for asking a question." data-id="element-462" data-mp="1aj34cbnwq31e" />

      <section className="bg-cream" data-id="element-463" data-mp="14otyehng8jon">
        <div className="mx-auto max-w-shell px-6 pb-16 md:px-8 md:pb-20" data-id="element-464" data-mp="14pv9un8dn86w">
          <PricingTiers data-id="element-465" data-mp="19nn1dg540sak" />
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-466" data-mp="1xcpsrfpjpzi9">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-467" data-mp="1xdr2ngutsfye">
          <SectionHeading eyebrow="Treatment costs" title="What each one costs on top of membership." intro="This is the pharmacy's price plus a flat handling fee we tell you about. It does not go up when your dose does." data-id="element-468" data-mp="22bslq1s94f9v" />

          <div className="mt-10 overflow-hidden rounded-brand border border-ink/10 bg-cream" data-id="element-469" data-mp="2gbahx27vmut9">
            <table className="w-full text-left" data-id="element-470" data-mp="2l20k66l0ahjq">
              <caption className="sr-only" data-id="element-471" data-mp="2l2si3o22srd6">Monthly cost by {brand.name} treatment</caption>
              <thead data-id="element-472" data-mp="2uwy38qptg5dp">
                <tr className="border-b border-ink/10 text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-473" data-mp="2uxhbffpnzfej">
                  <th scope="col" className="px-6 py-4 font-normal" data-id="element-474" data-mp="2pwtiuplseqi7">
                    Treatment
                  </th>
                  <th scope="col" className="hidden px-6 py-4 font-normal sm:table-cell" data-id="element-475" data-mp="2gy83t4fixtm6">
                    Category
                  </th>
                  <th scope="col" className="hidden px-6 py-4 font-normal md:table-cell" data-id="element-476" data-mp="2c06isdyjfk82">
                    Form
                  </th>
                  <th scope="col" className="px-6 py-4 text-right font-normal" data-id="element-477" data-mp="276bwd2rjsr5a">
                    From
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-478" data-mp="1y91l3heqd20d">
                {rows.map(product => <tr key={product.slug} className="border-b border-ink/10 transition-colors last:border-b-0 hover:bg-cream-deep/60" data-id="element-479" data-mp="1t96p4zg3lm8g">
                    <th scope="row" className="px-6 py-4 font-normal" data-id="element-480" data-mp="3s1frjub35cm">
                      <Link to={`/shop/${product.slug}`} className="font-serif text-base font-semibold text-ink transition-colors hover:text-brick" data-id="element-481" data-mp="3tw12z9hozh8">
                        {product.name}
                      </Link>
                    </th>
                    <td className="hidden px-6 py-4 text-sm text-ink-muted sm:table-cell" data-id="element-482" data-mp="3h6emiba9keru">
                      {categories.find(item => item.slug === product.category)?.name}
                    </td>
                    <td className="hidden px-6 py-4 text-sm text-ink-muted md:table-cell" data-id="element-483" data-mp="3q7cq4sgsik9w">
                      {product.form}
                    </td>
                    <td className="px-6 py-4 text-right text-base font-semibold text-ink" data-id="element-484" data-mp="3uzdwjxra1gs0">
                      ${product.price}
                      <span className="ml-1 text-xs font-normal text-ink-muted" data-id="element-485" data-mp="2ye3vbeoz259k">
                        {product.cadence}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-muted" data-id="element-486" data-mp="2efn6g8d3ayrr">
            These are starting monthly prices for the medication, and assume you are a member. We
            send HSA and FSA paperwork for every charge without you having to ask.
          </p>
        </div>
      </section>

      <section className="bg-cream" data-id="element-487" data-mp="321jxfwjg5hof">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-488" data-mp="2x4jzya0nxn8x">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-489" data-mp="2x6o32pqqmi85">
            <SectionHeading eyebrow="FAQ" title="The questions people actually ask." data-id="element-490" data-mp="3c12gmarj8zff" />
            <FaqList items={faqs} data-id="element-491" data-mp="373sr6v458nrl" />
          </div>
        </div>
      </section>

      <CtaBand data-id="element-492" data-mp="1hmrr0034618l" />
    </main>;
}